import { Box, HStack, Text, VStack, Slider, SliderTrack, SliderFilledTrack, SliderThumb } from '@chakra-ui/react'
import { describeEdgeFilterKey } from '../../../utils/functions/krakenUtils'

const EDGE_TYPE_COLORS: Record<string, string> = {
  similar_phone: '#22c55e',
  similar_email: '#22c55e',
  similar_name: '#eab308',
  similar_address: '#eab308',
  similar_platform_seller_id: '#ef4444',
  same_registrant: '#f97316',
  same_ip: '#f97316',
  same_postcode: '#22c55e',
  sold_by: '#818cf8',
  listed_on: '#8b5cf6',
  links_to: '#6d28d9',
  on_platform: '#a78bfa',
  registered_by: '#4338ca',
  hosted_by: '#6366f1',
  linked_to: '#7c3aed',
}

interface EdgeFilterPanelProps {
  availableEdgeTypes: Array<{ label: string; count: number }>
  activeFilters: Set<string>
  onFilterChange: (filters: Set<string>) => void
  /** Minimum edge weight to show. Edges without a weight always show. */
  minWeight?: number
  onMinWeightChange?: (value: number) => void
  /** Upper bound of the weight slider — defaults to 100, but graphs whose edge
   *  weights live in a small range (e.g. Neptune's 1-7) pass their own max. */
  weightMax?: number
  /** Slider step — defaults to 5 (good for 0-100), small-range graphs pass 1. */
  weightStep?: number
}

const EDGE_TYPE_LABELS: Record<string, string> = {
  similar_phone: 'Shared Phone',
  similar_email: 'Shared Email',
  similar_name: 'Similar Name',
  similar_address: 'Shared Address',
  similar_platform_seller_id: 'Same Seller ID',
  sold_by: 'Sold By',
  listed_on: 'Listed On',
  links_to: 'Links To',
  same_registrant: 'Same Registrant',
  same_ip: 'Same IP',
  same_postcode: 'Same Postcode',
  same_name: 'Same Name',
  same_phone: 'Same Phone',
  same_email: 'Same Email',
  same_address: 'Same Address',
  has_phone: 'Phone',
  has_email: 'Email',
  has_address: 'Address',
  discovery: 'Discovered',
  on_platform: 'On Platform',
  registered_by: 'Registered By',
  hosted_by: 'Hosted By',
  linked_to: 'Manual Link',
}

// Pretty-print any relation label: discovery subcategory (discovery:<kind>) →
// "Discovered · social post", else mapped name, else Title-Case the snake_case.
const humanizeRelation = (label: string): string =>
  describeEdgeFilterKey(label).label ||
  EDGE_TYPE_LABELS[label] ||
  label.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

// Dot colour for a relation row: discovery subcategories use their own palette.
const relationColor = (label: string): string =>
  EDGE_TYPE_COLORS[label] || describeEdgeFilterKey(label).color || '#a78bfa'

const EdgeFilterPanel = ({ availableEdgeTypes, activeFilters, onFilterChange, minWeight = 0, onMinWeightChange, weightMax = 100, weightStep = 5 }: EdgeFilterPanelProps) => {
  const toggleFilter = (label: string) => {
    const next = new Set(activeFilters)
    if (next.has(label)) {
      next.delete(label)
    } else {
      next.add(label)
    }
    onFilterChange(next)
  }

  const allActive = activeFilters.size === availableEdgeTypes.length

  const toggleAll = () => {
    if (allActive) {
      onFilterChange(new Set())
    } else {
      onFilterChange(new Set(availableEdgeTypes.map((e) => e.label)))
    }
  }

  if (availableEdgeTypes.length === 0) return null

  return (
    <Box
      bg="rgba(255, 255, 255, 0.92)"
      backdropFilter="blur(12px)"
      borderRadius="14px"
      border="1px solid"
      borderColor="blackAlpha.100"
      boxShadow="0 4px 16px rgba(15, 23, 42, 0.08)"
      // Height follows the content. The cap only exists so a graph with many
      // edge types can't outgrow the canvas — it stays clear of typical counts,
      // and the relations list is what scrolls if it ever engages.
      maxH="calc(100vh - 120px)"
      display="flex"
      flexDirection="column"
      overflow="hidden"
    >
      <HStack justify="space-between" px={2.5} pt={2} pb={1} flexShrink={0}>
        <Text fontSize="10px" fontWeight={700} color="gray.500" textTransform="uppercase" letterSpacing="0.06em">
          Relations
        </Text>
        <Text
          as="button"
          type="button"
          onClick={toggleAll}
          fontSize="10px"
          fontWeight={600}
          color="purple.500"
          px={1.5}
          py={0.5}
          borderRadius="5px"
          transition="background 0.15s"
          _hover={{ bg: 'purple.50' }}
        >
          {allActive ? 'Clear all' : 'Select all'}
        </Text>
      </HStack>

      <VStack align="stretch" gap={0} px={1.5} pb={1} overflowY="auto" minH={0} flex="0 1 auto">
        {availableEdgeTypes.map(({ label, count }) => {
          const isActive = activeFilters.has(label)
          return (
            <HStack
              as="button"
              type="button"
              key={label}
              aria-pressed={isActive}
              onClick={() => toggleFilter(label)}
              gap={2}
              px={1.5}
              py={1}
              borderRadius="6px"
              textAlign="left"
              transition="background 0.12s"
              _hover={{ bg: 'blackAlpha.50' }}
            >
              <Box
                w="15px"
                h="15px"
                borderRadius="4px"
                border="1.5px solid"
                borderColor={isActive ? relationColor(label) : 'gray.300'}
                bg={isActive ? relationColor(label) : 'transparent'}
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
                transition="all 0.12s"
              >
                {isActive && (
                  <svg width="9" height="9" viewBox="0 0 8 8" fill="none">
                    <path d="M1.5 4L3 5.5L6.5 2" stroke="white" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </Box>
              <Text
                fontSize="12px"
                fontWeight={500}
                color={isActive ? 'gray.700' : 'gray.400'}
                flex={1}
                noOfLines={1}
                transition="color 0.12s"
              >
                {humanizeRelation(label)}
              </Text>
              <Text
                fontSize="10px"
                fontWeight={600}
                sx={{ fontVariantNumeric: 'tabular-nums' }}
                color={isActive ? 'gray.500' : 'gray.300'}
                flexShrink={0}
              >
                {count}
              </Text>
            </HStack>
          )
        })}
      </VStack>

      {onMinWeightChange && (
        <Box px={2.5} pt={2} pb={2.5} borderTop="1px solid" borderColor="blackAlpha.100" flexShrink={0}>
          <HStack justify="space-between" mb={1.5}>
            <Text fontSize="10px" fontWeight={700} color="gray.500" textTransform="uppercase" letterSpacing="0.06em">
              Min strength
            </Text>
            <Text
              fontSize="10px"
              fontWeight={600}
              sx={{ fontVariantNumeric: 'tabular-nums' }}
              color={minWeight > 0 ? 'purple.600' : 'gray.400'}
              bg={minWeight > 0 ? 'purple.50' : 'transparent'}
              px={1.5}
              py={0.5}
              borderRadius="5px"
            >
              {minWeight > 0 ? `≥ ${minWeight}` : 'All'}
            </Text>
          </HStack>
          {/* Inset by half the thumb width — the thumb is centred on its value,
              so at 0 / max it would otherwise overhang the panel's padding. */}
          <Box px="7px">
            <Slider
              aria-label="Minimum connection weight"
              min={0}
              max={weightMax}
              step={weightStep}
              value={minWeight}
              onChange={onMinWeightChange}
              colorScheme="purple"
              focusThumbOnChange={false}
            >
              <SliderTrack h="4px" borderRadius="full" bg="gray.200">
                <SliderFilledTrack />
              </SliderTrack>
              <SliderThumb
                boxSize="14px"
                border="2px solid"
                borderColor="purple.500"
                boxShadow="0 1px 4px rgba(15, 23, 42, 0.2)"
                _focusVisible={{ boxShadow: '0 0 0 3px rgba(139, 92, 246, 0.35)' }}
              />
            </Slider>
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default EdgeFilterPanel
